import linksthree from "../../../../services/linksthree";
import { LinkButton } from "../../../../types";
import { WIZARD_DELAYS } from "../constants";
import { BaseStepDependencies } from "./stepTypes";

interface LinksStepDependencies extends BaseStepDependencies {
  addUserMessage: (content: string) => void;
  showFinishedStep: () => void;
}

// Estado temporário para acumular os links durante o wizard
const linksAccumulator: LinkButton[] = [];
let waitingForLinkUrl = false;
let currentLinkType: { label: string; icon: string } | null = null;

// Tipos de links comuns
const LINK_TYPES = [
  { label: "Instagram", icon: "📸", urlPrefix: "https://instagram.com/" },
  { label: "WhatsApp", icon: "💬", urlPrefix: "https://wa.me/" },
  { label: "Facebook", icon: "👥", urlPrefix: "https://facebook.com/" },
  { label: "LinkedIn", icon: "💼", urlPrefix: "https://linkedin.com/in/" },
  { label: "Site Pessoal", icon: "🌐", urlPrefix: "https://" },
  { label: "YouTube", icon: "📺", urlPrefix: "https://youtube.com/" },
  { label: "TikTok", icon: "🎵", urlPrefix: "https://tiktok.com/@" },
  { label: "Twitter/X", icon: "🐦", urlPrefix: "https://x.com/" },
];

export const startLinksConfiguration = (deps: LinksStepDependencies) => {
  const { addBotMessage } = deps;

  // Reset do acumulador
  linksAccumulator.length = 0;
  waitingForLinkUrl = false;
  currentLinkType = null;

  setTimeout(() => {
    addBotMessage(
      "Quase terminando! 🎉\n\nAgora vamos configurar seus links sociais. Eles aparecerão no seu site profissional para seus alunos te encontrarem.",
      { wizardType: "info", delay: WIZARD_DELAYS.MEDIUM },
    );
  }, 800);

  setTimeout(() => {
    addBotMessage(
      "Você gostaria de adicionar links sociais? (Instagram, WhatsApp, Site, etc.)",
      {
        wizardType: "question",
        delay: WIZARD_DELAYS.MEDIUM,
        actions: [
          {
            label: "Sim, Adicionar Links",
            onClick: () => showLinkOptions(deps),
            variant: "primary" as const,
          },
          {
            label: "Não, Pular",
            onClick: () => handleSkipLinks(deps),
            variant: "secondary" as const,
          },
        ],
      },
    );
  }, WIZARD_DELAYS.VERY_LONG);
};

const showLinkOptions = (deps: LinksStepDependencies) => {
  const { addUserMessage, addBotMessage, advanceToStep } = deps;

  addUserMessage("Sim, adicionar links");

  setTimeout(() => {
    addBotMessage("Ótimo! Escolha qual tipo de link você quer adicionar:", {
      wizardType: "question",
      delay: WIZARD_DELAYS.MEDIUM,
      actions: LINK_TYPES.slice(0, 4).map((linkType) => ({
        label: `${linkType.icon} ${linkType.label}`,
        onClick: () => handleLinkTypeSelection(linkType, deps),
        variant: "secondary" as const,
      })),
    });
  }, WIZARD_DELAYS.MEDIUM);

  setTimeout(() => {
    addBotMessage("Ou escolha outros:", {
      wizardType: "question",
      delay: WIZARD_DELAYS.MEDIUM,
      actions: [
        ...LINK_TYPES.slice(4).map((linkType) => ({
          label: `${linkType.icon} ${linkType.label}`,
          onClick: () => handleLinkTypeSelection(linkType, deps),
          variant: "secondary" as const,
        })),
        {
          label: "Pular por Enquanto",
          onClick: () => handleSkipLinks(deps),
          variant: "secondary" as const,
        },
      ],
    });
  }, WIZARD_DELAYS.LONG);

  advanceToStep("links", 0);
};

const handleLinkTypeSelection = (
  linkType: { label: string; icon: string; urlPrefix: string },
  deps: LinksStepDependencies,
) => {
  const { addUserMessage, addBotMessage, setAwaitingUserInput, advanceToStep } =
    deps;

  addUserMessage(`${linkType.icon} ${linkType.label}`);

  currentLinkType = linkType;
  waitingForLinkUrl = true;

  setTimeout(() => {
    let exampleText = "";
    if (linkType.label === "Instagram") {
      exampleText = "Exemplo: https://instagram.com/seu_usuario";
    } else if (linkType.label === "WhatsApp") {
      exampleText = "Exemplo: https://wa.me/5511999999999";
    } else if (linkType.label === "Site Pessoal") {
      exampleText = "Exemplo: https://seumeusite.com.br";
    } else {
      exampleText = `Cole o link completo (começando com https://) do seu ${linkType.label}`;
    }

    addBotMessage(
      `Perfeito! Cole aqui o **link completo** do seu ${linkType.label} (não use só o nome de usuário ou número).\n\n${exampleText}`,
      { wizardType: "question", delay: WIZARD_DELAYS.MEDIUM },
    );
  }, WIZARD_DELAYS.MEDIUM);

  setTimeout(() => {
    advanceToStep("links", 0);
    setAwaitingUserInput(true);
  }, WIZARD_DELAYS.LONG);
};

export const handleLinksResponse = (
  userInput: string,
  deps: LinksStepDependencies,
) => {
  const { addUserMessage, addBotMessage, setAwaitingUserInput } = deps;

  // Se estamos aguardando URL de um link
  if (waitingForLinkUrl && currentLinkType) {
    // Mostra a mensagem do usuário no chat
    addUserMessage(userInput);

    waitingForLinkUrl = false;
    const linkType = currentLinkType;
    currentLinkType = null;

    const trimmedInput = userInput.trim();

    // Só aceita links completos (URLs); rejeita nome de usuário, telefone ou texto sem protocolo
    const looksLikeUrl =
      trimmedInput.startsWith("http://") || trimmedInput.startsWith("https://");

    let normalizedUrl = trimmedInput;
    let isValidUrl = false;

    if (looksLikeUrl) {
      try {
        new URL(normalizedUrl);
        isValidUrl = true;
      } catch {
        isValidUrl = false;
      }
    }

    if (!looksLikeUrl || !isValidUrl) {
      setTimeout(() => {
        addBotMessage(
          "❌ Preciso do **link completo** (começando com https://).\n\nNão use apenas nome de usuário ou número de telefone — copie o link inteiro da barra de endereço do navegador.",
          { wizardType: "error", delay: WIZARD_DELAYS.MEDIUM },
        );
      }, WIZARD_DELAYS.MEDIUM);

      setTimeout(() => {
        waitingForLinkUrl = true;
        currentLinkType = linkType;
        setAwaitingUserInput(true);
      }, WIZARD_DELAYS.LONG);
      return;
    }

    // Adiciona ao acumulador
    linksAccumulator.push({
      id: `temp_${Date.now()}_${Math.random()}`,
      label: linkType.label,
      url: normalizedUrl,
      clicks: 0,
      enabled: true,
    });

    setTimeout(() => {
      addBotMessage(
        `✅ Link ${linkType.icon} ${linkType.label} adicionado com sucesso!\n\nDeseja adicionar mais algum link?`,
        {
          wizardType: "question",
          delay: WIZARD_DELAYS.MEDIUM,
          actions: [
            {
              label: "Sim, Adicionar Mais",
              onClick: () => showMoreLinkOptions(deps),
              variant: "primary" as const,
            },
            {
              label: "Não, Finalizar",
              onClick: () => void saveLinksAndContinue(deps),
              variant: "secondary" as const,
            },
          ],
        },
      );
    }, WIZARD_DELAYS.MEDIUM);
  } else {
    // Se não está aguardando link, ignora input inesperado
    console.warn(
      "Input recebido no step links mas não está aguardando URL. Estado:",
      {
        waitingForLinkUrl,
        currentLinkType,
        userInput,
      },
    );
  }
};

const showMoreLinkOptions = (deps: LinksStepDependencies) => {
  const { addUserMessage, addBotMessage } = deps;

  addUserMessage("Sim, adicionar mais");

  // Filtra links que ainda não foram adicionados
  const addedLabels = new Set(linksAccumulator.map((link) => link.label));
  const remainingLinkTypes = LINK_TYPES.filter(
    (lt) => !addedLabels.has(lt.label),
  );

  if (remainingLinkTypes.length > 0) {
    setTimeout(() => {
      addBotMessage(
        "Qual outro link você quer adicionar? Aqui estão as opções ainda não adicionadas:",
        {
          wizardType: "question",
          delay: WIZARD_DELAYS.MEDIUM,
          actions: remainingLinkTypes.slice(0, 4).map((linkType) => ({
            label: `${linkType.icon} ${linkType.label}`,
            onClick: () => handleLinkTypeSelection(linkType, deps),
            variant: "secondary" as const,
          })),
        },
      );
    }, WIZARD_DELAYS.MEDIUM);

    if (remainingLinkTypes.length > 4) {
      setTimeout(() => {
        addBotMessage("Outros:", {
          wizardType: "question",
          delay: WIZARD_DELAYS.MEDIUM,
          actions: [
            ...remainingLinkTypes.slice(4).map((linkType) => ({
              label: `${linkType.icon} ${linkType.label}`,
              onClick: () => handleLinkTypeSelection(linkType, deps),
              variant: "secondary" as const,
            })),
            {
              label: "Finalizar Links",
              onClick: () => void saveLinksAndContinue(deps),
              variant: "primary" as const,
            },
          ],
        });
      }, WIZARD_DELAYS.LONG);
    }
  } else {
    setTimeout(() => {
      addBotMessage(
        "Você já adicionou todos os tipos de links disponíveis! 🎉",
        { wizardType: "info", delay: WIZARD_DELAYS.MEDIUM },
      );
    }, WIZARD_DELAYS.MEDIUM);

    setTimeout(() => {
      void saveLinksAndContinue(deps);
    }, WIZARD_DELAYS.LONG);
  }
};

const handleSkipLinks = (deps: LinksStepDependencies) => {
  const { addUserMessage, addBotMessage, advanceToStep, showFinishedStep } =
    deps;

  addUserMessage("Não, pular");

  setTimeout(() => {
    addBotMessage(
      "Sem problemas! Você pode adicionar seus links sociais depois no painel.",
      { wizardType: "info", delay: WIZARD_DELAYS.MEDIUM },
    );
  }, WIZARD_DELAYS.MEDIUM);

  setTimeout(() => {
    addBotMessage("Vamos finalizar então! 🎉", {
      wizardType: "info",
      delay: WIZARD_DELAYS.MEDIUM,
    });
  }, WIZARD_DELAYS.LONG);

  setTimeout(() => {
    advanceToStep("finished", 0);
    showFinishedStep();
  }, WIZARD_DELAYS.VERY_LONG);
};

const saveLinksAndContinue = async (deps: LinksStepDependencies) => {
  const {
    userId,
    addUserMessage,
    addBotMessage,
    advanceToStep,
    setIsProcessing,
    showFinishedStep,
  } = deps;

  addUserMessage("Não, finalizar");

  if (linksAccumulator.length === 0) {
    handleSkipLinks(deps);
    return;
  }

  setTimeout(() => {
    addBotMessage(
      `Perfeito! Salvando seus ${linksAccumulator.length} link(s)...`,
      {
        wizardType: "info",
        delay: WIZARD_DELAYS.MEDIUM,
      },
    );
  }, WIZARD_DELAYS.MEDIUM);

  setTimeout(async () => {
    if (!userId) {
      addBotMessage(
        "❌ Erro: usuário não identificado. Vamos pular por enquanto.",
        {
          wizardType: "error",
          delay: WIZARD_DELAYS.MEDIUM,
        },
      );
      setTimeout(() => {
        handleSkipLinks(deps);
      }, WIZARD_DELAYS.LONG);
      return;
    }

    setIsProcessing(true);

    try {
      // Remove os IDs temporários antes de salvar
      const linksToSave = linksAccumulator.map((link) => ({
        ...link,
        id: link.id.startsWith("temp_") ? "" : link.id,
      }));

      console.log("📤 Salvando links:", linksToSave);

      const response = await linksthree.putButton(Number(userId), linksToSave);

      console.log("✅ Resposta do salvamento de links:", response);

      if (response) {
        addBotMessage(
          `✅ Links salvos com sucesso!\n\nTotal: ${linksAccumulator.length} link(s) configurado(s).`,
          { wizardType: "confirmation", delay: WIZARD_DELAYS.MEDIUM },
        );

        // Limpa o acumulador após salvar com sucesso
        linksAccumulator.length = 0;

        setTimeout(() => {
          addBotMessage("Estamos quase lá... 🎉", {
            wizardType: "info",
            delay: WIZARD_DELAYS.MEDIUM,
          });
        }, WIZARD_DELAYS.LONG);

        setTimeout(() => {
          advanceToStep("finished", 0);
          showFinishedStep();
        }, WIZARD_DELAYS.VERY_LONG);
      } else {
        addBotMessage("❌ Erro ao salvar links. Vamos pular por enquanto.", {
          wizardType: "error",
          delay: WIZARD_DELAYS.MEDIUM,
        });
        handleSkipLinks(deps);
      }
    } catch (error) {
      console.error("Erro ao salvar links:", error);
      addBotMessage("❌ Erro ao salvar links. Vamos pular por enquanto.", {
        wizardType: "error",
        delay: WIZARD_DELAYS.MEDIUM,
      });
      handleSkipLinks(deps);
    } finally {
      setIsProcessing(false);
    }
  }, WIZARD_DELAYS.LONG);
};
